import { TFunction } from "i18next";

const compactFormatter = new Intl.NumberFormat("da-DK", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const integerFormatter = new Intl.NumberFormat("da-DK", {
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat("da-DK", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const rateFormatter = new Intl.NumberFormat("da-DK", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 6,
});

export function formatInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return integerFormatter.format(n);
}

export function formatCompact(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return compactFormatter.format(n);
}

export function formatMoney(
  amount: number | null | undefined,
  currency: string = "DKK",
): string {
  if (amount === null || amount === undefined) return "—";
  return `${decimalFormatter.format(amount)} ${currency}`;
}

export function formatRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "—";
  return rateFormatter.format(rate);
}

export function formatDateShort(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("da-DK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("da-DK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatRelative(
  iso: string | null | undefined,
  t: TFunction,
): string {
  if (!iso) return t("adminApiKeys.never");
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const min = 60_000;
  const hr = 60 * min;
  const day = 24 * hr;
  if (diffMs < min) return t("adminApiKeys.time.justNow");
  if (diffMs < hr)
    return t("adminApiKeys.time.minutesAgo", {
      minutes: Math.floor(diffMs / min),
    });
  if (diffMs < day)
    return t("adminApiKeys.time.hoursAgo", { hours: Math.floor(diffMs / hr) });
  if (diffMs < 30 * day)
    return t("adminApiKeys.time.daysAgo", { days: Math.floor(diffMs / day) });
  return formatDateShort(iso);
}

// The last four characters of the API's masked key hint ("km_live_…4f2a" ->
// "4f2a"): the only part of a key the page ever shows, used by the delete
// action's label and confirmation. A hint shorter than four characters is
// returned as it is.
export function keyLast4(keyIdPrefix: string): string {
  return keyIdPrefix.slice(-4);
}

const BILLING_PERIOD_KEYS: Record<string, string> = {
  weekly: "adminApiKeys.billingPeriodLower.weekly",
  monthly: "adminApiKeys.billingPeriodLower.monthly",
  quarterly: "adminApiKeys.billingPeriodLower.quarterly",
  yearly: "adminApiKeys.billingPeriodLower.yearly",
};

export function planSummary(
  plan: {
    planType: string;
    billingPeriod: string | null;
    baseFee: number | null;
    includedCalls: number | null;
    overageRate: number | null;
    currency: string;
  } | null,
  t: TFunction,
): string {
  if (!plan) return t("adminApiKeys.status.noPlan");
  if (plan.planType === "FreeTierCap") {
    return t("adminApiKeys.planSummaryFree", {
      calls: formatCompact(plan.includedCalls ?? 0),
    });
  }
  if (plan.planType === "FlatPerCall") {
    return t("adminApiKeys.perCall", {
      rate: formatRate(plan.overageRate),
      currency: plan.currency,
    });
  }
  const periodKey = plan.billingPeriod
    ? BILLING_PERIOD_KEYS[plan.billingPeriod.toLowerCase()]
    : undefined;
  const period = plan.billingPeriod
    ? periodKey
      ? t(periodKey)
      : plan.billingPeriod.toLowerCase()
    : "";
  return `${formatMoney(plan.baseFee, plan.currency)} ${period}`.trim();
}
